import React, { useState, useEffect } from "react";
import { listPhysicians as getPhysicians, deletePhysician } from "../api/physicians.js";

const SUBSPECIALTY_LABELS = {
  // Oncology
  "hematology": "Hematology",
  "solid-breast-gyn": "Breast/GYN",
  "solid-thoracic-gi": "Thoracic/GI",
  "solid-gu": "GU/Prostate",
  "medical-general": "General Med Onc",
  "medical-integrative": "Integrative",
  // Orthopedic
  "joint-replacement-hip-knee": "Joint Replacement",
  "joint-replacement-shoulder": "Shoulder Replacement",
  "sports-medicine-operative": "Sports Med (Op)",
  "sports-medicine-non-op": "Sports Med (Non-Op)",
  "hand-upper-extremity": "Hand & Upper",
  "foot-ankle": "Foot & Ankle",
  "spine": "Spine",
  "trauma-fracture-care": "Trauma",
  "pediatric-orthopedics": "Pediatric Ortho",
  "physical-medicine-rehab": "PM&R",
  "pain-management": "Pain Management",
  "general-orthopedics": "General Ortho",
};

export default function PhysicianList({ navigate }) {
  const [physicians, setPhysicians] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    getPhysicians()
      .then(setPhysicians)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const handleDelete = async (id, name) => {
    if (!confirm(`Delete ${name}? This cannot be undone.`)) return;
    try {
      await deletePhysician(id);
      refresh();
    } catch (err) {
      alert("Delete failed: " + err.message);
    }
  };

  const filtered = physicians.filter((p) => {
    const q = search.toLowerCase();
    if (!q) return true;
    const subs = Array.isArray(p.subspecialties) ? p.subspecialties : [p.subspecialty].filter(Boolean);
    const offices = Array.isArray(p.offices) ? p.offices : [];
    return (
      (p.id || "").toLowerCase().includes(q) ||
      (p.name || "").toLowerCase().includes(q) ||
      subs.some((s) => s.toLowerCase().includes(q)) ||
      (p.focusArea || "").toLowerCase().includes(q) ||
      (p.location?.city || "").toLowerCase().includes(q) ||
      (p.location?.name || "").toLowerCase().includes(q) ||
      offices.some((o) => (o.name || "").toLowerCase().includes(q) || (o.city || "").toLowerCase().includes(q)) ||
      (p.bodyParts || []).some((b) => b.toLowerCase().includes(q)) ||
      (p.conditionsTreated || []).some((c) => c.toLowerCase().includes(q))
    );
  });

  if (loading) return <div style={s.loading}>Loading physicians...</div>;

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Physicians</h1>
          <span style={s.count}>{filtered.length} {filtered.length === 1 ? "doctor" : "doctors"}</span>
        </div>
        <div style={s.headerActions}>
          <button style={s.btnOutline} onClick={() => navigate("physician-match")}>
            Try Match
          </button>
          <button style={s.btnPrimary} onClick={() => navigate("physician-editor")}>
            + New Physician
          </button>
        </div>
      </div>

      <div style={s.searchWrap}>
        <svg style={s.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2">
          <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
        </svg>
        <input
          style={s.search}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, specialty, condition, location..."
        />
      </div>

      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>ID</th>
              <th style={s.th}>Name</th>
              <th style={s.th}>Subspecialty</th>
              <th style={s.th}>Location</th>
              <th style={s.th}>Conditions</th>
              <th style={s.th}>Accepting</th>
              <th style={{ ...s.th, textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} style={s.tr}>
                <td style={s.td}><span style={s.id}>{p.id}</span></td>
                <td style={s.td}>
                  <div style={s.nameCell}>
                    <span style={s.name}>{p.name}</span>
                    <span style={s.subtle}>{p.credentials}</span>
                  </div>
                </td>
                <td style={s.td}>
                  <div style={s.subBadgeWrap}>
                    {(Array.isArray(p.subspecialties) && p.subspecialties.length > 0
                      ? p.subspecialties
                      : [p.subspecialty].filter(Boolean)
                    ).map((sub) => (
                      <span key={sub} style={s.subBadge}>
                        {SUBSPECIALTY_LABELS[sub] || sub}
                      </span>
                    ))}
                  </div>
                </td>
                <td style={s.td}>
                  {Array.isArray(p.offices) && p.offices.length > 0 ? (
                    <div style={s.locCell}>
                      <span style={s.locName}>{p.offices[0].name || p.offices[0].city || "—"}</span>
                      <span style={s.subtle}>
                        {p.offices.length === 1
                          ? `${p.offices[0].city || ""}, ${p.offices[0].state || ""}`
                          : `${p.offices.length} offices`}
                      </span>
                    </div>
                  ) : p.location ? (
                    <div style={s.locCell}>
                      <span style={s.locName}>{p.location.name || "—"}</span>
                      <span style={s.subtle}>{p.location.city}, {p.location.state}</span>
                    </div>
                  ) : (
                    <span style={s.subtle}>No location</span>
                  )}
                </td>
                <td style={s.td}>
                  <span style={s.condCount}>
                    {(p.conditionsTreated || []).length} conditions
                  </span>
                </td>
                <td style={s.td}>
                  {p.acceptingNewPatients ? (
                    <span style={s.acceptBadge}>Accepting</span>
                  ) : (
                    <span style={s.closedBadge}>Closed</span>
                  )}
                </td>
                <td style={{ ...s.td, textAlign: "right" }}>
                  <div style={s.actions}>
                    <button style={s.actionBtn} onClick={() => navigate("physician-editor", { physicianId: p.id })} title="Edit">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                    <button style={s.actionBtnDanger} onClick={() => handleDelete(p.id, p.name)} title="Delete">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div style={s.empty}>
            {physicians.length === 0
              ? "No physicians yet. Click '+ New Physician' to add one."
              : "No physicians match your search."}
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  loading: { padding: 60, textAlign: "center", color: "#999", fontSize: 14 },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "20px 32px 0" },
  title: { fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em" },
  count: { fontSize: 13, color: "#999", background: "#f5f5f5", padding: "2px 10px", borderRadius: 10, marginLeft: 8 },
  headerActions: { display: "flex", gap: 8 },
  btnPrimary: {
    padding: "7px 16px", background: "#0f0f0f", color: "#fff", border: "1px solid #0f0f0f",
    borderRadius: 6, fontSize: 13, fontWeight: 500,
  },
  btnOutline: {
    padding: "7px 16px", background: "#fff", color: "#0f0f0f", border: "1px solid #e5e5e5",
    borderRadius: 6, fontSize: 13, fontWeight: 500,
  },
  searchWrap: { position: "relative", margin: "16px 32px 0" },
  searchIcon: { position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" },
  search: {
    width: "100%", padding: "9px 12px 9px 36px", border: "1px solid #e5e5e5",
    borderRadius: 8, fontSize: 14, outline: "none", color: "#0f0f0f",
  },
  tableWrap: { margin: "12px 32px 32px", border: "1px solid #e5e5e5", borderRadius: 10, overflow: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    padding: "10px 16px", textAlign: "left", fontSize: 12, fontWeight: 500,
    color: "#666", borderBottom: "1px solid #e5e5e5", background: "#fafafa", whiteSpace: "nowrap",
  },
  tr: { borderBottom: "1px solid #f0f0f0" },
  td: { padding: "10px 16px", fontSize: 13, verticalAlign: "middle" },
  id: { fontFamily: "monospace", fontSize: 12, color: "#999" },
  nameCell: { display: "flex", flexDirection: "column" },
  name: { fontWeight: 500, color: "#0f0f0f" },
  subtle: { fontSize: 11, color: "#999", marginTop: 2 },
  subBadgeWrap: { display: "flex", flexWrap: "wrap", gap: 4 },
  subBadge: {
    display: "inline-block", padding: "2px 10px", borderRadius: 10,
    background: "#eef4ff", color: "#1d4ed8", fontSize: 11, fontWeight: 500,
  },
  locCell: { display: "flex", flexDirection: "column" },
  locName: { fontWeight: 500, color: "#0f0f0f" },
  condCount: { fontSize: 12, color: "#666" },
  acceptBadge: {
    display: "inline-block", padding: "2px 8px", borderRadius: 10,
    background: "#ecfdf5", color: "#059669", fontSize: 11, fontWeight: 500,
  },
  closedBadge: {
    display: "inline-block", padding: "2px 8px", borderRadius: 10,
    background: "#fef2f2", color: "#dc2626", fontSize: 11, fontWeight: 500,
  },
  actions: { display: "flex", gap: 2, justifyContent: "flex-end" },
  actionBtn: {
    width: 30, height: 30, border: "none", borderRadius: 6, background: "transparent",
    color: "#666", display: "flex", alignItems: "center", justifyContent: "center",
  },
  actionBtnDanger: {
    width: 30, height: 30, border: "none", borderRadius: 6, background: "transparent",
    color: "#999", display: "flex", alignItems: "center", justifyContent: "center",
  },
  empty: { padding: "30px", textAlign: "center", color: "#999", fontSize: 13 },
};
